// src/features/links/Pagination.tsx
// IR-1386 (Tomer): numbered pages replace "Load more" — "< 1 2 3 … 47 >".
//
// Why a page picker beats the button it replaces: with ~8,600 links the old
// control could only ever walk forward one page at a time, so the far end of
// the queue was ~170 clicks away and there was no way to say where you were.
import { useMemo } from "react";

interface PaginationProps {
  page: number;
  pageCount: number;
  onPageChange: (page: number) => void;
  /** Dims the control while the next page is in flight. */
  busy?: boolean;
}

/** A gap in the page run, rendered as an ellipsis. */
const GAP = "gap" as const;
type Slot = number | typeof GAP;

/**
 * Page numbers to render: always the first and last page, plus a window around
 * the current one, with gaps collapsed to a single ellipsis. Keeping the ends
 * always visible is what makes "jump to the end" a single click.
 */
export function pageSlots(page: number, pageCount: number, windowSize = 1): Slot[] {
  if (pageCount <= 1) return [1];
  const wanted = new Set<number>([1, pageCount]);
  for (let p = page - windowSize; p <= page + windowSize; p++) {
    if (p >= 1 && p <= pageCount) wanted.add(p);
  }
  const sorted = [...wanted].sort((a, b) => a - b);
  const slots: Slot[] = [];
  let prev = 0;
  for (const p of sorted) {
    // A single missing page is shown as itself — an ellipsis hiding one number
    // is bigger than the number and costs a click.
    if (prev && p - prev === 2) slots.push(prev + 1);
    else if (prev && p - prev > 2) slots.push(GAP);
    slots.push(p);
    prev = p;
  }
  return slots;
}

export function Pagination({ page, pageCount, onPageChange, busy = false }: PaginationProps) {
  const slots = useMemo(() => pageSlots(page, pageCount), [page, pageCount]);
  if (pageCount <= 1) return null;

  const atStart = page <= 1;
  const atEnd = page >= pageCount;

  return (
    <nav
      className={`flex items-center justify-center gap-1 pt-2 ${busy ? "opacity-60" : ""}`}
      aria-label="Pagination"
    >
      <button
        type="button"
        className="iris-btn px-2 text-sm"
        onClick={() => onPageChange(page - 1)}
        disabled={atStart}
        aria-label="Previous page"
      >
        ‹
      </button>
      {slots.map((slot, i) =>
        slot === GAP ? (
          // Keyed by the page it follows, not by the array index: a gap always
          // sits between two known pages, so this is stable and unique.
          <span
            key={`gap-after-${slots[i - 1]}`}
            className="px-1 text-sm text-[var(--iris-fg-3)]"
            aria-hidden="true"
          >
            …
          </span>
        ) : (
          <button
            key={slot}
            type="button"
            className={`iris-btn px-2 text-sm ${slot === page ? "iris-btn--primary" : ""}`}
            onClick={() => onPageChange(slot)}
            aria-label={`Page ${slot}`}
            aria-current={slot === page ? "page" : undefined}
          >
            {slot}
          </button>
        ),
      )}
      <button
        type="button"
        className="iris-btn px-2 text-sm"
        onClick={() => onPageChange(page + 1)}
        disabled={atEnd}
        aria-label="Next page"
      >
        ›
      </button>
    </nav>
  );
}
